import type { XOR } from '@headlessui/react/dist/types'
import type { CommonListResponse, CommonResponse } from 'types/api'
import type {
  BuktiDukung,
  CommonMultiPeriodMeta,
  Jenjang,
  KinerjaRoleType,
  KinerjaSelection,
  KinerjaStatus,
  KinerjaTipe,
  KinerjaWithRole,
  PerilakuKerjaPayload,
} from 'types/kinerja'
import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

export type KinerjaKsStatus = 'NOT_FOUND' | 'DRAFT' | 'READY'

export enum PanelKinerjaSourceTabId {
  'PraktikKinerjaKepalaSekolah' = 'praktik-kinerja-kepala-sekolah',
  'PengembanganKompetensi' = 'pengembangan-kompetensi',
  'RencanaHasilKerjaEkstra' = 'rencana-hasil-kerja-ekstra',
}

export interface KinerjaKs {
  id: string
  tahun: number
  period: number
  status: KinerjaStatus
  pangkatGolongan: string
  jenjang: string
  kepemimpinanSatuanPendidikan: Array<number>
  pengembanganKompetensi: Array<KinerjaSelection>
  rencanaHasilKerjaEkstra: Array<number>
  perilakuKerja: Array<PerilakuKerjaPayload>
  tipe: KinerjaTipe

  ekinEligible: boolean
  kategoriAsn: boolean
  isPadan: boolean
}

export interface KinerjaKsKepemimpinanSatuanPendidikan {
  id: number
  nama?: string
  definisi?: string
  targetKuantitas: string
  targetWaktu: number
  buktiDukung: Array<BuktiDukung>
  perspektif?: string
  rencanaHasilKerja: string
  iconName: string
}

export type KinerjaKsRencanaHasilKerjaExtra =
  KinerjaKsKepemimpinanSatuanPendidikan & {
    position?: {
      order: number
      placeOnTop: boolean
    }
  }

export interface KinerjaKsUtamaDetail {
  id: number
  rencanaHasilKerja: string
  targetKuantitasMin?: number
  targetKuantitasMax?: number
  targetWaktu: number
  bobot: number
  buktiDukung: BuktiDukung[]
  targetKuantitas?: string
  perspektif?: string
  catatan: string
}

export interface ContentKinerjaKSUtama {
  jenjangId: number
  jenjang: string
  kepemimpinanSatuanPendidikan: Array<KinerjaKsKepemimpinanSatuanPendidikan>
  pengembanganKompetensi: Array<KinerjaKsUtamaDetail>
  rencanaHasilKerjaEkstra: Array<KinerjaKsRencanaHasilKerjaExtra>
}

export type ResponseContentKinerjaKSUtama =
  CommonResponse<ContentKinerjaKSUtama>

export interface KinerjaKsPerencanaanTabBaseProps {
  skp: KinerjaKs
  isSubmitting?: boolean
  onClickNext?: (data: UpdateSKPKSPayloadWithoutNpsn) => void
  onClickSave?: (data: UpdateSKPKSPayloadWithoutNpsn) => void
}

export type UpdateSKPKSPayload = KinerjaWithRole<{
  kepemimpinanSatuanPendidikan?: Array<number>
  pengembanganKompetensi?: Array<KinerjaSelection>
  tugasTambahan?: Array<KinerjaSelection>
  perilakuKerja?: Array<PerilakuKerjaPayload>
  npsn?: string
}>

export type CreateSKPPayload = KinerjaWithRole<{
  pangkatGolongan: string
  jenjang: string
  npsn?: string
}>

export type UpdateSKPKSPayloadWithoutNpsn = Omit<
  UpdateSKPKSPayload,
  'npsn' | 'role'
>

export type UpsertSKPKSPayload = XOR<CreateSKPPayload, UpdateSKPKSPayload>

export type ResponseKinerjaKs = CommonResponse<KinerjaKs>
export type ResponseKinerjaKsByPs = CommonResponse<
  KinerjaKs & { namaKs: string; jenjang: Jenjang; satuanPendidikan: string }
>

export type ResponsePendingPerencanaanKsByPs = CommonListResponse<
  { nama: string; jabatan: string; satuanPendidikan: string },
  CommonMultiPeriodMeta
>

export type PerencanaanKsByDinasState = 'ALL' | 'READY' | 'DRAFT'
export type PerencanaanKsByDinasStateLower =
  Lowercase<PerencanaanKsByDinasState>
export type PerencanaanKsByDinasRole = 'KS_DEFINITIF' | 'KS_PLT'
export type PerencanaanKsByDinasTipe = 'SPF' | 'SPNF'
export type PerencanaanKsByDinasTipeCounts = Partial<
  Record<PerencanaanKsByDinasTipe, number>
>
export type PerencanaanKsByDinasData = {
  nama: string
  jabatan: string
  satuanPendidikan: string
  state: PerencanaanKsByDinasState
  npsn: string
  ptkId: string
}
export type PerencanaanKsByDinasMeta = CommonMultiPeriodMeta & {
  state: PerencanaanKsByDinasState
  role: PerencanaanKsByDinasRole
  counts: Record<PerencanaanKsByDinasStateLower, number>
  countsTipe?: PerencanaanKsByDinasTipeCounts
}
export type ResponseAllPerencanaanKsByDinas = CommonListResponse<
  PerencanaanKsByDinasData,
  PerencanaanKsByDinasMeta
>

export interface ContentKinerjaByPsAndDinas {
  jenjangId: number
  jenjang: string
  kepemimpinanSatuanPendidikan: Array<KinerjaKsKepemimpinanSatuanPendidikan>
  pengembanganKompetensi: Array<KinerjaKsUtamaDetail>
  rencanaHasilKerjaEkstra: Array<KinerjaKsRencanaHasilKerjaExtra>
}

export type ResponseContentKinerjaByPsAndDinas =
  CommonResponse<ContentKinerjaByPsAndDinas>

export interface School {
  schoolName: string
  npsn: string
  role: KinerjaRoleType
  state: KinerjaKsStatus
}

export type Schools = Array<School>

export type KinerjaPsDinasRoleType = 'DINAS' | 'PS' | 'TK_GPK' | 'PS_SPNF'

export type PengawasProfile = {
  nama: string
  roles: Array<KinerjaPsDinasRoleType>
  namaWilayah?: string // Eg. Provinsi Jawa Barat
  namaLembaga?: string // Eg. Dinas Pendidikan dan Olahraga Provinsi Jawa Barat
}

export type TabFilterRoles = 'KS' | 'GURU' | string

export type PenilaianSchool = Pick<
  PelaksanaanSchool,
  | 'schoolName'
  | 'npsn'
  | 'role'
  | 'confirmationStatus'
  | 'statusSatuanPendidikan'
>
